import React from "react";

import { Permission } from "~/models/dto";

import { Api, type Monitor } from "../common/openapi";
import { usePermissions } from "../common/use-permissions";
import { useErrorMessage } from "../components/_notification-error";
import { CtxMenuItem } from "../components/react/CtxMenu/CtxMenuItem";
import { CtxPopoverMenu } from "../components/react/CtxMenu/CtxPopoverMenu";
import { LinkTo } from "../components/react/link";
import { yesNoModalConfirm } from "../components/react/modal/modal-utils";
import { notification } from "../components/react/notification/Notification";
import { isHTTPError } from "../generic/request";
import { useDeleteMonitor, useInvalidateMonitorOverviewQuery, useUpdateMonitorStatus } from "../queries";
import { MONITORS_PATH } from "../routes/Routes";

interface MonitorContextProps {
	monitor: Monitor;
	icon?: React.ReactNode;
}

export const MonitorContextMenu: React.FC<MonitorContextProps> = ({ monitor, icon }) => {
	const permissions = usePermissions();
	const hasCreateMonitorsPermission = permissions?.hasSystemPermission(Permission.CreateMonitors);
	const hasUpdateMonitorsPermission = permissions?.hasSystemPermission(Permission.UpdateMonitors);
	const hasDeleteMonitorsPermission = permissions?.hasSystemPermission(Permission.DeleteMonitors);

	const { displayErrorMessage } = useErrorMessage();
	const isEditDisabled = !(monitor.canEdit || monitor.canClone);
	const isDeleteDisabled = !monitor.canEdit || monitor.sourceDetails != null;

	const invalidateMonitorOverviewList = useInvalidateMonitorOverviewQuery();

	const { isLoading: isDeleting, mutate: deleteMonitor } = useDeleteMonitor({
		onSuccess: () => {
			notification.success({
				message: `Successfully deleted monitor "${monitor.name}"`,
			});

			return invalidateMonitorOverviewList();
		},

		onError: (error) => {
			displayErrorMessage({
				message: `Failed to delete monitor "${monitor.name}"`,
				error: isHTTPError(error) ? error : undefined,
			});
		},
	});

	const { isLoading: isUpdating, mutate: updateMonitorStatus } = useUpdateMonitorStatus({
		onSuccess: (result) => {
			notification.success({
				message: `Monitor ${result.name} ${result.status === Api.MonitorStatusValue.ENABLED ? "enabled" : "disabled"}.`,
			});

			return invalidateMonitorOverviewList();
		},
		onError: (error) => {
			displayErrorMessage({
				message: `Can't change status for monitor ${monitor.id}.`,
				error,
			});
		},
	});

	const onDelete = async () => {
		const isConfirmed = await yesNoModalConfirm(`Are you sure you want to delete the "${monitor.name}" monitor?`);

		if (isConfirmed) {
			deleteMonitor(monitor.id);
		}
	};

	return (
		<CtxPopoverMenu data-testid="context-menu-menu" ellipsisClassName="ml-0" icon={icon}>
			{hasCreateMonitorsPermission && hasUpdateMonitorsPermission && (
				<CtxMenuItem
					disabled={isEditDisabled || isUpdating || isDeleting}
					key="contextMenu__monitors-overview__edit_condition"
					label={
						<LinkTo
							className="block"
							params={{ id: monitor.id }}
							search={["timeRange", "timestamp", "detachedFilters"]}
							to={MONITORS_PATH}
						>
							Edit
						</LinkTo>
					}
					title={isEditDisabled && "This is a system monitor which can not be edited at the moment."}
				/>
			)}

			{hasUpdateMonitorsPermission && monitor.status === Api.MonitorStatusValue.ENABLED && (
				<CtxMenuItem
					disabled={isUpdating || isDeleting}
					key="contextMenu__monitors-overview__enabled"
					label="Disable"
					onClick={() => updateMonitorStatus({ monitorId: monitor.id, status: Api.MonitorStatusValue.DISABLED })}
				/>
			)}

			{hasUpdateMonitorsPermission && monitor.status === Api.MonitorStatusValue.DISABLED && (
				<CtxMenuItem
					disabled={isUpdating || isDeleting}
					key="contextMenu__monitors-overview__disabled"
					label="Enable"
					onClick={() => updateMonitorStatus({ monitorId: monitor.id, status: Api.MonitorStatusValue.ENABLED })}
				/>
			)}

			{hasDeleteMonitorsPermission && (
				<CtxMenuItem
					disabled={isDeleteDisabled || isUpdating || isDeleting}
					label="Delete"
					onClick={() => void onDelete()}
					title={isDeleteDisabled && "This is a system monitor which can not be deleted."}
				/>
			)}
		</CtxPopoverMenu>
	);
};
